import React, { useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { CHIP_STYLE_TYPES } from '@CommonConstants';
import { CHOICE_RESULT_KEYS } from '@Services/Editor/Constants';

import { deleteResultPropertyChipState, setChangeStatus } from '@Services/Editor';
import useContextMenu from '@Hooks/useContextMenu';

import { ReactComponent as StatesPropertiesSVG } from '@Assets/Svgs/ic_properties_states.svg';
import { ReactComponent as HealthStatesSVG } from '@Assets/Svgs/ic_states_health.svg';
import { ReactComponent as MentalStatesSVG } from '@Assets/Svgs/ic_states_mental.svg';
import { ReactComponent as MoneyStatesSVG } from '@Assets/Svgs/ic_states_money.svg';
import Chip from '@Components/Common/Chip';
import ChangeStatusContextMenu from './ChangeStatusContextMenu';

const CHANGE_STATUS_KEYS = {
  HEALTH: 'bodyHealth',
  MENTAL: 'mentalHealth',
  MONEY: 'money',
};

const CHANGE_STATUS_KOR = {
  [CHANGE_STATUS_KEYS.HEALTH]: '체력',
  [CHANGE_STATUS_KEYS.MENTAL]: '멘탈',
  [CHANGE_STATUS_KEYS.MONEY]: '돈',
};

const ChangeStatusChip = ({ processId, choiceId, resultId }) => {
  const dispatch = useDispatch();
  // const { changeStatus, resultPropertyChipState } = useSelector(
  //   ({ editor }) => editor.processes[processId].choices[choiceId].results[resultId],
  // );

  const { changeStatus, resultPropertyChipState } = useSelector(({ editor }) => {
    const result = editor.processes[processId].choices[choiceId].results.find(v => v.resultId === resultId);
    return result || {}; // result가 없을 경우 기본값으로 빈 객체 반환
  });

  const changeStatusChipRef = useRef(null);
  const changeStatusRef = useRef(null);

  const { isContextMenuVisible, handleShowContextMenuClick, handleCloseContextMenuClick } = useContextMenu(false);

  // const toStringByChangeStatus = () => {
  //   return Object.entries(changeStatus)
  //     .map(([key, count]) => {
  //       const prefixStatus = count > 0 ? '+' : '';

  //       return `${CHANGE_STATUS_KOR[key]} ${prefixStatus}${count}`;
  //     })
  //     .join(', ');
  // };

  const toStringByChangeStatus = () => {
    // Check if all values in changeStatus are 0
    const allValuesZero = Object.values(changeStatus).every(value => value === 0);

    // If all values are 0, return null
    if (allValuesZero) {
      return '';
    }

    // If not all values are 0, proceed with the current logic
    return Object.entries(changeStatus)
      .filter(([, count]) => count !== 0)
      .map(([key, count]) => {
        const prefixStatus = count > 0 ? '+' : '';
        return `${CHANGE_STATUS_KOR[key]} ${prefixStatus}${count}`;
      })
      .join(', ');
  };

  const handleStepperItemClick = useCallback(
    (type, count) => {
      dispatch(setChangeStatus({ processId, choiceId, resultId, status: { [type]: count } }));
    },
    [changeStatus, dispatch],
  );

  const handleDeleteChangeStatusChipClick = useCallback(() => {
    dispatch(
      deleteResultPropertyChipState({
        processId,
        choiceId,
        resultId,
        type: CHOICE_RESULT_KEYS.CHANGE_STATUS,
        state: false,
      }),
    );
  }, [dispatch]);

  const CHANGE_STATUS_CONTEXT_MENU_ITEMS = [
    {
      type: CHANGE_STATUS_KEYS.HEALTH,
      title: CHANGE_STATUS_KOR[CHANGE_STATUS_KEYS.HEALTH],
      min: -3,
      max: 3,
      Icon: <HealthStatesSVG />,
      onClick: count => handleStepperItemClick(CHANGE_STATUS_KEYS.HEALTH, count),
    },
    {
      type: CHANGE_STATUS_KEYS.MENTAL,
      title: CHANGE_STATUS_KOR[CHANGE_STATUS_KEYS.MENTAL],
      min: -3,
      max: 3,
      Icon: <MentalStatesSVG />,
      onClick: count => handleStepperItemClick(CHANGE_STATUS_KEYS.MENTAL, count),
    },
    {
      type: CHANGE_STATUS_KEYS.MONEY,
      title: CHANGE_STATUS_KOR[CHANGE_STATUS_KEYS.MONEY],
      min: -3,
      max: 3,
      Icon: <MoneyStatesSVG />,
      onClick: count => handleStepperItemClick(CHANGE_STATUS_KEYS.MONEY, count),
    },
  ];

  if (!resultPropertyChipState.changeStatus) {
    return null;
  }
  return (
    <ChangeStatusChipContainer ref={changeStatusChipRef}>
      <Chip
        chipRef={changeStatusRef}
        type={CHIP_STYLE_TYPES.ICON}
        placeholder="변화 없음"
        value={toStringByChangeStatus()}
        Icon={StatesPropertiesSVG}
        onFocus={handleShowContextMenuClick}
        onBlur={handleCloseContextMenuClick}
        onDelete={handleDeleteChangeStatusChipClick}
      />

      {isContextMenuVisible && (
        <ChangeStatusContextMenu
          targetRef={changeStatusChipRef}
          isContextMenuVisible={isContextMenuVisible}
          CHANGE_STATUS_CONTEXT_MENU_ITEMS={CHANGE_STATUS_CONTEXT_MENU_ITEMS}
          changeStatus={changeStatus}
        />
      )}
    </ChangeStatusChipContainer>
  );
};

ChangeStatusChip.propTypes = {
  processId: PropTypes.number.isRequired,
  choiceId: PropTypes.number.isRequired,
  resultId: PropTypes.number.isRequired,
};

const ChangeStatusChipContainer = styled.div``;

export default React.memo(ChangeStatusChip);
